import * as faceapi from '@vladmandic/face-api';

/**
 * Enterprise LIMS Facial Biometrics Engine Service.
 * Self-hosted local neural network models for detection, 68-point landmarks,
 * 128-D vector embedding extraction, and real-time liveness analysis.
 */
class FaceApiService {
  constructor() {
    this.modelsLoaded = false;
    this.loadingPromise = null;
    this.modelPath = '/models';
    this.detectorOptions = null;
  }

  /**
   * Returns whether models are already warmed up in memory.
   */
  isModelsLoaded() {
    return this.modelsLoaded;
  }

  /**
   * Initializes and loads pre-trained quantized neural network models from local static assets.
   * Cached to guarantee one-time asynchronous load without duplicate network fetch.
   */
  async loadModels() {
    if (this.modelsLoaded) return true;
    if (this.loadingPromise) return this.loadingPromise;

    this.loadingPromise = (async () => {
      try {
        console.info('[Biometric Face Engine] Initializing models from', this.modelPath);

        // Load Tiny Face Detector (fast inference), Landmarks (68-point), and Face Recognition (ResNet-34)
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(this.modelPath),
          faceapi.nets.faceLandmark68Net.loadFromUri(this.modelPath),
          faceapi.nets.faceRecognitionNet.loadFromUri(this.modelPath),
        ]);

        // Pre-instantiate single detector options object to eliminate garbage collection per frame
        this.detectorOptions = new faceapi.TinyFaceDetectorOptions({
          inputSize: 320,
          scoreThreshold: 0.5,
        });

        this.modelsLoaded = true;
        console.info('[Biometric Face Engine] All facial recognition models loaded successfully.');
        return true;
      } catch (err) {
        console.error('[Biometric Face Engine] Failed to load models:', err);
        this.loadingPromise = null;
        throw new Error('Unable to initialize face recognition engine. Please verify local model files.');
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Detects single face with full 68-point landmarks and 128-D feature vector.
   * @param {HTMLVideoElement|HTMLCanvasElement|HTMLImageElement} mediaElement
   * @returns {Promise<{ detection: object, descriptor: number[], landmarks: object, quality: number, guideMessage: string } | null>}
   */
  async detectFaceAndDescriptor(mediaElement) {
    if (!this.modelsLoaded) {
      await this.loadModels();
    }

    const options = this.detectorOptions || new faceapi.TinyFaceDetectorOptions({
      inputSize: 320,
      scoreThreshold: 0.5,
    });

    const result = await faceapi
      .detectSingleFace(mediaElement, options)
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (!result) {
      return null;
    }

    const descriptorArray = Array.from(result.descriptor);
    const quality = this.evaluateFaceQuality(result, mediaElement);
    const guideMessage = this.getFramingGuidance(result, mediaElement);

    return {
      raw: result,
      detection: result.detection,
      landmarks: result.landmarks,
      descriptor: descriptorArray,
      quality,
      guideMessage,
    };
  }

  /**
   * Calculates Eye Aspect Ratio (EAR) from 68 facial landmarks to evaluate blink/liveness.
   * Left eye landmarks: 36-41; Right eye landmarks: 42-47.
   */
  calculateEyeAspectRatio(landmarks) {
    if (!landmarks || !landmarks.positions) return 0.3;
    const pts = landmarks.positions;

    const eyeRatio = (p1, p2, p3, p4, p5, p6) => {
      const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
      const vertical1 = dist(p2, p6);
      const vertical2 = dist(p3, p5);
      const horizontal = dist(p1, p4);
      return (vertical1 + vertical2) / (2.0 * horizontal);
    };

    const leftEAR = eyeRatio(pts[36], pts[37], pts[38], pts[39], pts[40], pts[41]);
    const rightEAR = eyeRatio(pts[42], pts[43], pts[44], pts[45], pts[46], pts[47]);

    return (leftEAR + rightEAR) / 2.0;
  }

  /**
   * Checks whether the face is properly centered and sized in the camera viewfinder.
   */
  getFramingGuidance(result, mediaElement) {
    const box = result.detection.box;
    const width = mediaElement.videoWidth || mediaElement.width || 640;
    const height = mediaElement.videoHeight || mediaElement.height || 480;

    const faceCenterX = box.x + box.width / 2;
    const faceCenterY = box.y + box.height / 2;
    const frameCenterX = width / 2;
    const frameCenterY = height / 2;

    const faceCoverageRatio = (box.width * box.height) / (width * height);

    if (faceCoverageRatio < 0.08) {
      return 'Please move closer to the camera';
    }
    if (faceCoverageRatio > 0.45) {
      return 'Please step slightly back from the camera';
    }

    const horizontalOffset = Math.abs(faceCenterX - frameCenterX) / width;
    if (horizontalOffset > 0.18) {
      return 'Please center your face inside the framing guide';
    }

    const verticalOffset = Math.abs(faceCenterY - frameCenterY) / height;
    if (verticalOffset > 0.20) {
      return 'Please position your face at eye level';
    }

    return 'Perfect framing. Hold steady.';
  }

  /**
   * Evaluates quality metric 0.0 to 1.0 based on detection score and proportion.
   */
  evaluateFaceQuality(result, mediaElement) {
    const score = result.detection.score || 0.7;
    const box = result.detection.box;
    const width = mediaElement.videoWidth || mediaElement.width || 640;
    const height = mediaElement.videoHeight || mediaElement.height || 480;

    const areaRatio = (box.width * box.height) / (width * height);
    const sizeQuality = Math.min(1.0, areaRatio / 0.15);

    const quality = score * 0.6 + sizeQuality * 0.4;
    return Math.min(1.0, Math.max(0.1, Number(quality.toFixed(2))));
  }

  /**
   * Helper: Calculates Euclidean distance between two 128-D descriptors on client if needed.
   */
  calculateDistance(descA, descB) {
    return faceapi.euclideanDistance(descA, descB);
  }
}

export const faceApiService = new FaceApiService();
export default faceApiService;
